/**
 * Project the text nodes below root into a flat string while retaining enough
 * DOM information to map each projected character back to its source node.
 */
// Subtrees that are not in the document a reader sees: the untaken branch of a
// conditional, a display:none region, editor scaffolding, transient search
// highlights.
const HIDDEN_SUBTREE_SELECTOR =
  ".lp-hide-element, .SOCE, .EOCE, .SKIP, [data-markjs], script, style, [style*='display:none' i], [style*='display: none' i]";

// Rendered, but carrying no text worth indexing. `.word-indent` is the spacer
// after a list marker — twelve non-breaking spaces clipped to `height: 0` and
// sized by `width` — so the projection must skip it (it would contribute
// characters the model would then have to reason about) while anything drawing
// the paragraph must keep it. It is the whole gap between "(i)" and the text.
const LAYOUT_ONLY_SUBTREE_SELECTOR = '.word-indent';

// What the projection skips.
export const SKIPPED_SUBTREE_SELECTOR = `${HIDDEN_SUBTREE_SELECTOR}, ${LAYOUT_ONLY_SUBTREE_SELECTOR}`;

// What an annotation card drops, which is not the same list.
//
// A card is a picture of the paragraph; the projection is a text index. They
// agree about the untaken branch — a card quoting text the projection never
// read describes a paragraph the model was never given — and they disagree
// about layout. Reusing the projection's selector here removed the spacer and
// closed "(i)" up against the text it labels, and because the first line then
// packed differently under `text-align: justify`, every line after it moved too.
export const ANNOTATION_HIDDEN_SUBTREE_SELECTOR = HIDDEN_SUBTREE_SELECTOR;
// `generate-table` is listed first and matched first: production markup carries
// `list=""` on it (office_lease/00001:929, retail_lease/00001:284), so a plain
// `[list]` test would misclassify a whole generated table as an auto-number.
const ATOM_SELECTOR = 'generate-table, lease-var, crr, [list]';
// Generated whole-row changes use these tags as structural wrappers. They must
// stay transparent so the existing table-row projection can still read the
// `<tr>`; only prose markers become d/i tokens.
const TRACKED_CHANGE_SELECTOR =
  'del:not([data-table-row-change]), ins:not([data-table-row-change])';
const TRACKED_CHANGE_TOKEN_PREFIX = { d: '⟦d', i: '⟦i' };
// One grammar, one emitter, three readers: this file issues the tokens,
// document_patch_schema validates a model string against them,
// document_patch_compiler parses one back into kind/ordinal/label and repairs a
// mistranscribed ordinal, and document_note_placement resolves a reference
// through them. The alphabet is exported so a new kind is added in exactly one
// place. Adding `d`/`i` here alone left the readers on `[fxnt]` and they
// disagreed loudly: `atomParts` threw `Invalid atom token: ⟦d31:not⟧` and the
// schema refused `find` as "not ordinal-form" for quoting the projection's own
// text — on every document carrying a counterparty redline.
export const ATOM_KINDS = 'fxntdi';
export const ATOM_TOKEN_PATTERN = `⟦([${ATOM_KINDS}])(\\d+):([^⟦⟧\\r\\n]*)⟧`;
const ATOM_TOKEN = new RegExp(ATOM_TOKEN_PATTERN, 'g');
const ATOM_BRACKETS = /[⟦⟧]/g;
const ZERO_WIDTH_CHARACTERS = /[\u200B\uFEFF]/g;
// One diff item may contain megabytes of converter HTML. Keep at most 16k
// projected label characters per diff-index; replacements split that budget
// evenly between their deletion and insertion so either side remains useful.
// Truncation is surfaced in `projectionFlags`, keyed by diff-index, so the
// review card can disclose that the model saw only part of the change.
export const MAX_CHANGE_ITEM_CHARS = 16000;
// One source of truth for the escape set: if the escape and unescape sets ever
// drift apart the projection silently stops being reversible. The plain form is
// tested one character at a time while walking and must stay non-global — a /g
// regex's `test` advances `lastIndex` between calls.
const MARKDOWN_SYNTAX_CHARACTER = /[_*|#`\[\]]/;
const MARKDOWN_SYNTAX_CHARACTERS = new RegExp(
  MARKDOWN_SYNTAX_CHARACTER.source,
  'g',
);
const ESCAPED_MARKDOWN_SYNTAX_CHARACTERS = new RegExp(
  `\\\\(${MARKDOWN_SYNTAX_CHARACTER.source})`,
  'g',
);
const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6';
const TABLE_CELL_SELECTOR = 'td, th';
const STRUCTURAL_CHILD_SELECTOR = `${HEADING_SELECTOR}, p, ol, ul, li, table, thead, tbody, tfoot, tr, td, th`;

// Source indentation, not lease prose. The DOCX→HTML export pretty-prints the
// template, so a paragraph is written as `<p>\n\t\t<span>…</span>\n\t</p>` and
// every block sits on its own indented line: 4885 such nodes in
// retail_lease/00001 alone. A browser collapses them to at most one rendered
// space, and P6-04 gives a newline the meaning "block boundary", so emitting
// them verbatim would put a raw newline and tabs inside every paragraph and
// split every pipe row across several lines. A whitespace run carrying a line
// break or a tab is never authored lease content; a genuine inline space (a
// lone U+0020 between two runs) is, and stays a source-backed span.
function isLayoutWhitespace(node) {
  if (!/^\s*$/.test(node.data)) return false;
  if (/[\n\r\t]/.test(node.data)) return true;

  const parent = node.parentElement;
  return Boolean(
    parent &&
      Array.from(parent.children).some(child =>
        child.matches(STRUCTURAL_CHILD_SELECTOR),
      ),
  );
}

export function escapeForProjection(text) {
  return text.replace(MARKDOWN_SYNTAX_CHARACTERS, '\\$&');
}

export function unescapeFromProjection(text) {
  return text.replace(ESCAPED_MARKDOWN_SYNTAX_CHARACTERS, '$1');
}

/**
 * Split projected text at opaque automation-owned atoms. There is always one
 * more prose segment than atom, including empty segments when an atom starts
 * or ends the text. Keeping those empty boundaries is what lets the compiler
 * diff prose on either side without moving or rewriting the atom's DOM node.
 */
export function splitOnAtoms(text) {
  const prose = [];
  const atoms = [];
  let cursor = 0;

  ATOM_TOKEN.lastIndex = 0;
  for (
    let match = ATOM_TOKEN.exec(text);
    match;
    match = ATOM_TOKEN.exec(text)
  ) {
    prose.push(text.slice(cursor, match.index));
    atoms.push(match[0]);
    cursor = match.index + match[0].length;
  }
  prose.push(text.slice(cursor));

  return { prose, atoms };
}

function spanAtOrBefore(spans, offset) {
  let lower = 0;
  let upper = spans.length;

  // Find the first span that starts after the offset. Taking the span before
  // it deliberately right-biases a boundary shared by consecutive spans, so
  // the same offset always resolves to the start of the later source node.
  while (lower < upper) {
    const middle = lower + Math.floor((upper - lower) / 2);
    if (spans[middle].start <= offset) lower = middle + 1;
    else upper = middle;
  }

  return spans[lower - 1] || null;
}

function spanStartingBefore(spans, offset) {
  let lower = 0;
  let upper = spans.length;

  // Range ends need the opposite boundary bias from range starts. Find the
  // last span that starts strictly before the endpoint so an ignored DOM run
  // between two projection-contiguous spans is not accidentally selected.
  while (lower < upper) {
    const middle = lower + Math.floor((upper - lower) / 2);
    if (spans[middle].start < offset) lower = middle + 1;
    else upper = middle;
  }

  return spans[lower - 1] || null;
}

// Strictly inside, so an atom's own start and end are excluded: those are the
// boundaries between the atom and whatever sits beside it, not positions within
// the opaque token. Where prose abuts the atom they already resolved through the
// neighbouring source span above; where nothing abuts it (a block that opens or
// closes on an atom, or two adjacent atoms) they have no source position at all.
// Keeping the two apart matters because they are different refusals: addressing
// the middle of an atom is a patch trying to edit automation-owned content,
// while addressing its outer edge is merely an offset with no text to point at.
function atomInterior(index, offset) {
  const atom = spanAtOrBefore(index.atomSpans || [], offset);
  return atom && offset > atom.start && offset < atom.end ? atom : null;
}

// One source of truth for "is this even an offset into the projection". Both
// endpoint paths below run it: a fractional or out-of-bounds offset that skipped
// the check would be silently coerced by `Range.setEnd`, handing a caller a
// plausible-looking range built from an address the projection never issued.
function assertProjectedOffset(index, offset) {
  if (!Number.isInteger(offset) || offset < 0 || offset > index.text.length) {
    throw new RangeError(`Offset ${offset} is outside the text index`);
  }
}

export function resolve(index, offset) {
  assertProjectedOffset(index, offset);

  const span = spanAtOrBefore(index.spans, offset);
  if (span && offset >= span.start && offset <= span.end) {
    return {
      node: span.node,
      nodeOffset: span.nodeStart + offset - span.start,
    };
  }

  const atom = atomInterior(index, offset);
  if (atom) {
    throw new RangeError(
      `Offset ${offset} falls inside atom span ${atom.ordinal}`,
    );
  }

  throw new RangeError(`Offset ${offset} has no DOM source position`);
}

export function rangeFor(index, start, end) {
  if (start > end) {
    throw new RangeError(`Range start ${start} is after end ${end}`);
  }

  const startPosition = resolve(index, start);
  let endPosition = startPosition;

  if (start !== end) {
    // The span branch below never reaches `resolve`, so validate the offset
    // here or a non-integer `end` inside a span would reach `setEnd` and be
    // truncated instead of refused.
    assertProjectedOffset(index, end);
    const endingSpan = spanStartingBefore(index.spans, end);
    endPosition =
      endingSpan && end <= endingSpan.end
        ? {
            node: endingSpan.node,
            nodeOffset: endingSpan.nodeStart + end - endingSpan.start,
          }
        : resolve(index, end);
  }
  const range = startPosition.node.ownerDocument.createRange();
  range.setStart(startPosition.node, startPosition.nodeOffset);
  range.setEnd(endPosition.node, endPosition.nodeOffset);

  return range;
}

// The projection's read rules are also its write rules: a subtree buildTextIndex
// refuses to walk is a subtree markRange must refuse to mark. Both selectors are
// reused verbatim so the two can never drift apart.
const UNPROJECTED_SUBTREE_SELECTOR = `${SKIPPED_SUBTREE_SELECTOR}, ${ATOM_SELECTOR}, ${TRACKED_CHANGE_SELECTOR}`;

function unprojectedAncestor(node) {
  const parent = node.parentElement;
  return parent ? parent.closest(UNPROJECTED_SUBTREE_SELECTOR) : null;
}

/**
 * Mark the text selected by a DOM Range without moving it out of its existing
 * inline formatting ancestors. Each selected text run gets its own marker, so
 * consumers can group the fragments by attributes such as `diff-index` while
 * bold, italic, and other formatting remains intact.
 *
 * Only text the projection actually sources is marked. A range legitimately
 * spans an atom or a hidden branch — `resolve` refuses only offsets *inside* an
 * atom token, so a find covering "Tenant ⟦f0:Acme⟧ shall" is well-formed — and
 * those subtrees are stepped over rather than wrapped, leaving markers on the
 * prose either side. Deciding whether spanning an atom is *allowed* is the
 * compiler's `mutates_atom` ladder (P7-02), not this primitive's; its job is
 * only to not corrupt them on the way past.
 */
export function markRange(range, tag, attrs = {}) {
  if (range.collapsed) return [];
  if (
    range.startContainer.nodeType !== Node.TEXT_NODE ||
    range.endContainer.nodeType !== Node.TEXT_NODE
  ) {
    throw new TypeError('markRange boundaries must be text nodes');
  }

  // A boundary *inside* a skipped or atom subtree is malformed input rather
  // than content to step over: no offset in index.text addresses it, so no
  // caller can have meant it. Refuse loudly instead of marking nothing, which
  // would look like a successful no-op.
  [range.startContainer, range.endContainer].forEach(boundary => {
    const enclosing = unprojectedAncestor(boundary);
    if (enclosing) {
      throw new RangeError(
        `markRange boundary is inside a non-projected <${enclosing.tagName.toLowerCase()}> subtree`,
      );
    }
  });

  // Capture nodes and offsets before changing the DOM. Range boundary points
  // are live and splitText adjusts them, so reading an offset after an earlier
  // split can otherwise select the wrong substring (especially when both
  // boundaries are in the same text node).
  const selections = [];
  const collect = node => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (!range.intersectsNode(node)) return;
      // Source indentation projects as at most a synthetic space and owns no
      // span. Wrapping it would add a marker holding nothing visible, which the
      // partial-fragment labelling at lease_editor_controller.js:9611-9626
      // counts like any other, mislabelling the real runs around it.
      if (isLayoutWhitespace(node)) return;

      const start = node === range.startContainer ? range.startOffset : 0;
      const end =
        node === range.endContainer ? range.endOffset : node.data.length;
      if (start < end) selections.push({ node, start, end });
      return;
    }

    if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.matches(UNPROJECTED_SUBTREE_SELECTOR)
    ) {
      return;
    }

    node.childNodes.forEach(collect);
  };

  collect(range.commonAncestorContainer);

  return selections.map(({ node, start, end }) => {
    // Split the end first so the saved start offset still addresses the
    // original node. After both splits, selectedNode contains only marked text.
    if (end < node.data.length) node.splitText(end);
    const selectedNode = start > 0 ? node.splitText(start) : node;
    const marker = selectedNode.ownerDocument.createElement(tag);

    Object.entries(attrs).forEach(([name, value]) => {
      marker.setAttribute(name, value);
    });

    selectedNode.parentNode.replaceChild(marker, selectedNode);
    marker.appendChild(selectedNode);
    return marker;
  });
}

function atomKind(element) {
  if (element.matches('generate-table')) return 't';
  if (element.matches('lease-var')) return 'f';
  if (element.matches('crr')) return 'x';
  return 'n';
}

function trackedChangeKind(element) {
  return element.matches('del') ? 'd' : 'i';
}

function trackedChangeDiffIndex(element) {
  const raw = element.getAttribute('diff-index');
  if (!/^\d+$/.test(raw || '')) return null;

  const value = Number(raw);
  return Number.isSafeInteger(value) ? value : null;
}

// An atom label is projected text, so it obeys the same skip rules the walker
// applies to everything else — a false `lease-if` branch inside a `lease-var`
// must not reach index.text just because its subtree is summarised rather than
// walked. `innerText` is deliberately not used: jsdom leaves it undefined so no
// spec could exercise it, and in a browser it forces layout and inserts a
// newline for the `<br>` a Markdown-valued lease-var renders
// (lease_var_service.js:142, showdown simpleLineBreaks).
function atomText(element) {
  const parts = [];
  const collect = node => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.data);
      return;
    }
    if (
      node.nodeType !== Node.ELEMENT_NODE ||
      node.matches(SKIPPED_SUBTREE_SELECTOR)
    ) {
      return;
    }
    node.childNodes.forEach(collect);
  };

  element.childNodes.forEach(collect);

  return parts.join('');
}

// A token is one flat run: strip the zero-width characters P6-02 keeps out of
// index.text, strip the bracket pair so a label can never forge a token
// boundary, and collapse every whitespace run so no label spans a line.
function normalizeLabel(text) {
  return text
    .replace(ZERO_WIDTH_CHARACTERS, '')
    .replace(ATOM_BRACKETS, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function generatedTableLabel(element) {
  const visible = row => {
    const skipped = row.closest(SKIPPED_SUBTREE_SELECTOR);
    return !skipped || !element.contains(skipped);
  };
  const bodyRows = Array.from(element.querySelectorAll('tbody tr')).filter(
    visible,
  );
  const rows =
    bodyRows.length > 0
      ? bodyRows
      : Array.from(element.querySelectorAll('tr')).filter(visible);
  const rowLabel = row => {
    const firstCell = row.querySelector('th, td');
    return normalizeLabel(atomText(firstCell || row));
  };
  // A single-row table has one boundary, not a range: dedupe so the summary
  // reads "1 row, Year 1" rather than "1 row, Year 1–Year 1".
  const range = rows.length
    ? Array.from(new Set([rowLabel(rows[0]), rowLabel(rows[rows.length - 1])]))
        .filter(Boolean)
        .join('–')
    : '';
  const rowCount = `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`;

  return range ? `${rowCount}, ${range}` : rowCount;
}

function atomLabel(element, kind) {
  return kind === 't'
    ? generatedTableLabel(element)
    : normalizeLabel(atomText(element));
}

function atomFieldPath(element, kind) {
  if (kind === 'f') {
    return (
      element.getAttribute('name') ||
      element.getAttribute('ng-attr-name') ||
      null
    );
  }

  if (kind !== 't') return null;

  // List-backed generated tables name their source directly. Rent tables are
  // different: calculateRows derives `rowsData` from the lease model and the
  // directive marks the transcluded table with the owning deal-terms array
  // (`lease.rentRows` or `lease.renewalRows`). The projection is built only
  // after that directive has settled, so either attribute is authoritative.
  const generatedTable = element.querySelector('table[hl]');
  return (
    element.getAttribute('var') ||
    (generatedTable && generatedTable.getAttribute('hl')) ||
    null
  );
}

export function buildTextIndex(root) {
  const spans = [];
  const atomSpans = [];
  const projectionFlags = [];
  const textParts = [];
  const changeCharactersByKind = new Map();
  let offset = 0;
  let atomOrdinal = 0;
  let needsBlockBreak = false;
  // Set when a break was suppressed to a single space rather than a newline —
  // source indentation between two inline runs, or a block boundary inside a
  // table cell, where a newline would split a pipe row. Flushed lazily so a
  // following block break cancels it instead of leaving a trailing space.
  let pendingSpace = false;

  const emitSyntax = content => {
    if (!content) return;

    textParts.push(content);
    offset += content.length;
  };

  const lastCharacter = () =>
    textParts.length > 0 ? textParts[textParts.length - 1].slice(-1) : '';

  const ensureBlockBreak = () => {
    if (offset > 0 && lastCharacter() !== '\n') emitSyntax('\n');
    needsBlockBreak = false;
    pendingSpace = false;
  };

  const beforeContent = () => {
    if (needsBlockBreak) {
      ensureBlockBreak();
      return;
    }
    if (!pendingSpace) return;

    if (offset > 0 && !/\s/.test(lastCharacter())) emitSyntax(' ');
    pendingSpace = false;
  };

  const isInsideTableCell = element =>
    Boolean(element.closest(TABLE_CELL_SELECTOR));

  const openElement = element => {
    if (element.matches(HEADING_SELECTOR)) {
      ensureBlockBreak();
      emitSyntax(`${'#'.repeat(Number(element.tagName.slice(1)))} `);
      return;
    }

    if (element.matches('p')) {
      // A cell's paragraphs share one pipe row: separate them with a space so
      // "c/o Democo Ventures Inc." and the street below it neither run together
      // nor break the row across two lines.
      if (isInsideTableCell(element)) pendingSpace = true;
      else if (element !== root) ensureBlockBreak();
      return;
    }

    if (element.matches('li')) {
      ensureBlockBreak();
      const list = element.parentElement;
      const siblings = list
        ? Array.from(list.children).filter(child => child.matches('li'))
        : [];
      const marker = list?.matches('ol')
        ? `${siblings.indexOf(element) + 1}.`
        : '-';
      let ancestorLists = 0;
      let ancestor = element.parentElement;
      while (ancestor) {
        if (ancestor.matches('ol, ul')) ancestorLists += 1;
        ancestor = ancestor.parentElement;
      }
      emitSyntax(`${'  '.repeat(Math.max(0, ancestorLists - 1))}${marker} `);
      return;
    }

    if (element.matches('table')) {
      ensureBlockBreak();
      return;
    }

    if (element.matches('tr')) {
      ensureBlockBreak();
      emitSyntax('| ');
      return;
    }

    if (element.matches(TABLE_CELL_SELECTOR)) {
      const row = element.parentElement;
      const cells = row
        ? Array.from(row.children).filter(child =>
            child.matches(TABLE_CELL_SELECTOR),
          )
        : [];
      if (cells.indexOf(element) > 0) emitSyntax(' | ');
      return;
    }

    if (element.matches('br')) {
      if (isInsideTableCell(element)) pendingSpace = true;
      else needsBlockBreak = true;
    }
  };

  const closeElement = element => {
    if (element.matches('tr')) {
      emitSyntax(' |');
      needsBlockBreak = true;
      pendingSpace = false;
      return;
    }

    if (element.matches('p')) {
      if (isInsideTableCell(element)) pendingSpace = true;
      else if (element !== root) needsBlockBreak = true;
      return;
    }

    if (element.matches(`${HEADING_SELECTOR}, li, table`)) {
      needsBlockBreak = true;
    }
  };

  const emitAtom = element => {
    beforeContent();
    const kind = atomKind(element);
    const ordinal = atomOrdinal;
    atomOrdinal += 1;
    const label = atomLabel(element, kind);
    const token = `⟦${kind}${ordinal}:${label}⟧`;
    const end = offset + token.length;

    textParts.push(token);
    atomSpans.push({
      start: offset,
      end,
      kind,
      ordinal,
      label,
      element,
      fieldPath: atomFieldPath(element, kind),
    });
    offset = end;
  };

  const trackedChangeElements = () => {
    if (root.nodeType !== Node.ELEMENT_NODE) return [];

    const elements = root.matches(TRACKED_CHANGE_SELECTOR) ? [root] : [];
    elements.push(...root.querySelectorAll(TRACKED_CHANGE_SELECTOR));
    return elements.filter(element => {
      const skipped = element.closest(SKIPPED_SUBTREE_SELECTOR);
      const atom = element.closest(ATOM_SELECTOR);
      return (
        (!skipped || !root.contains(skipped)) &&
        (!atom || !root.contains(atom)) &&
        !element.closest('table')
      );
    });
  };

  const replacementDiffIndexes = new Set();
  const kindsByDiffIndex = new Map();
  trackedChangeElements().forEach(element => {
    const diffIndex = trackedChangeDiffIndex(element);
    if (diffIndex === null) return;

    const kinds = kindsByDiffIndex.get(diffIndex) || new Set();
    kinds.add(trackedChangeKind(element));
    kindsByDiffIndex.set(diffIndex, kinds);
  });
  kindsByDiffIndex.forEach((kinds, diffIndex) => {
    if (kinds.has('d') && kinds.has('i')) replacementDiffIndexes.add(diffIndex);
  });

  // A label sits inside ⟦⟧, which already delimits it, so it carries no
  // Markdown escaping — the rule every other atom kind follows and the one
  // escaping.test.js pins for an unset lease-var. The ceiling is therefore an
  // exact character count, taken at a code-point boundary so truncating a
  // change can never leave a lone surrogate half in the projection.
  const truncateLabel = (source, limit) => {
    if (source.length <= limit) return source;

    const label = source.slice(0, limit);
    return /[\uD800-\uDBFF]$/.test(label) ? label.slice(0, -1) : label;
  };

  const emitTrackedChange = element => {
    const kind = trackedChangeKind(element);
    const rawDiffIndex = element.getAttribute('diff-index');
    const diffIndex = trackedChangeDiffIndex(element);

    if (diffIndex === null) {
      projectionFlags.push({
        type: 'tracked_change_excluded',
        reason: 'invalid_diff_index',
        diffIndex: rawDiffIndex,
        changeKind: kind,
      });
      return;
    }

    if (element.closest('table')) {
      projectionFlags.push({
        type: 'tracked_change_excluded',
        reason: 'table_interior',
        diffIndex,
        changeKind: kind,
      });
      return;
    }

    // A change that projects to nothing is not a change anybody can advise on.
    //
    // Word records paragraph spacers, non-breaking spaces and the blank runs
    // between inserted articles as tracked changes like any other, and each one
    // was reaching the reader as its own token. It came back as "Adds no
    // substantive text" — dozens of times in a single import — and every one of
    // those cost a request and then rendered a card in the drafter's panel
    // saying nothing. One inserted article alone contributed a dozen.
    //
    // Excluded before the projection rather than filtered after the answer,
    // because the point is not to pay for the reading. Whitespace only: a lone
    // bracket or full stop is left in, since deleting one can change a sentence
    // and this should not be the code that decides it did not.
    const source = normalizeLabel(atomText(element));
    if (!source) {
      projectionFlags.push({
        type: 'tracked_change_excluded',
        reason: 'no_projected_text',
        diffIndex,
        changeKind: kind,
      });
      return;
    }

    beforeContent();
    const kindLimit = replacementDiffIndexes.has(diffIndex)
      ? Math.floor(MAX_CHANGE_ITEM_CHARS / 2)
      : MAX_CHANGE_ITEM_CHARS;
    const budgetKey = `${diffIndex}:${kind}`;
    const used = changeCharactersByKind.get(budgetKey) || 0;
    const remaining = Math.max(0, kindLimit - used);
    const label = truncateLabel(source, remaining);
    const token = `${TRACKED_CHANGE_TOKEN_PREFIX[kind]}${diffIndex}:${label}⟧`;
    const end = offset + token.length;
    const truncated = label.length < source.length;

    textParts.push(token);
    atomSpans.push({
      start: offset,
      end,
      kind,
      ordinal: diffIndex,
      label,
      element,
      fieldPath: null,
      truncated,
      sourceLength: source.length,
    });
    changeCharactersByKind.set(budgetKey, used + label.length);
    offset = end;

    if (truncated) {
      projectionFlags.push({
        type: 'tracked_change_truncated',
        diffIndex,
        changeKind: kind,
        sourceLength: source.length,
        projectedLength: label.length,
        maxCharacters: MAX_CHANGE_ITEM_CHARS,
      });
    }
  };

  const result = () => {
    const index = { text: textParts.join(''), spans, atomSpans };
    if (projectionFlags.length > 0) index.projectionFlags = projectionFlags;
    return index;
  };

  const nodeFilter = {
    acceptNode(node) {
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        node.matches(SKIPPED_SUBTREE_SELECTOR)
      ) {
        return NodeFilter.FILTER_REJECT;
      }
      if (node.parentElement && node.parentElement.closest(ATOM_SELECTOR)) {
        return NodeFilter.FILTER_REJECT;
      }
      if (
        node.parentElement &&
        node.parentElement.closest(TRACKED_CHANGE_SELECTOR)
      ) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  };

  // TreeWalker never applies its filter to the root itself.
  if (
    root.nodeType === Node.ELEMENT_NODE &&
    root.matches(SKIPPED_SUBTREE_SELECTOR)
  ) {
    return result();
  }

  if (root.nodeType === Node.ELEMENT_NODE && root.matches(ATOM_SELECTOR)) {
    emitAtom(root);
    return result();
  }

  if (
    root.nodeType === Node.ELEMENT_NODE &&
    root.matches(TRACKED_CHANGE_SELECTOR)
  ) {
    emitTrackedChange(root);
    return result();
  }

  // `ownerDocument` is null when root IS a document (DOMParser output), and a
  // text node directly under a DocumentFragment has no parentElement at all
  // (createContextualFragment output). Both are reachable ways to hand this a
  // parsed template, so neither may throw.
  const walker = (root.ownerDocument || root).createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    nodeFilter,
  );
  let current;
  let openPath = [];

  const pathFor = node => {
    const path = [];
    let element =
      node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;

    while (element && element !== root) {
      path.unshift(element);
      element = element.parentElement;
    }

    if (
      root.nodeType === Node.ELEMENT_NODE &&
      root.matches(`${HEADING_SELECTOR}, ol, ul, li, table, tr, td, th`)
    ) {
      path.unshift(root);
    }

    return path;
  };

  const moveTo = node => {
    const nextPath = pathFor(node);
    let commonLength = 0;

    while (
      commonLength < openPath.length &&
      commonLength < nextPath.length &&
      openPath[commonLength] === nextPath[commonLength]
    ) {
      commonLength += 1;
    }

    for (let index = openPath.length - 1; index >= commonLength; index -= 1) {
      closeElement(openPath[index]);
    }
    nextPath.slice(commonLength).forEach(openElement);
    openPath = nextPath;
  };

  while ((current = walker.nextNode())) {
    moveTo(current);

    if (
      current.nodeType === Node.ELEMENT_NODE &&
      current.matches(ATOM_SELECTOR)
    ) {
      emitAtom(current);
      continue;
    }

    if (
      current.nodeType === Node.ELEMENT_NODE &&
      current.matches(TRACKED_CHANGE_SELECTOR)
    ) {
      emitTrackedChange(current);
      continue;
    }

    if (current.nodeType !== Node.TEXT_NODE) {
      continue;
    }

    // Dropped, but remembered: it still separates whatever renders around it.
    if (isLayoutWhitespace(current)) {
      pendingSpace = true;
      continue;
    }

    beforeContent();

    const parent = current.parentElement;
    const editable = parent && parent.closest('.editable[section-id]');
    const pidElement = parent && parent.closest('[pid]');
    let nodeStart = 0;

    if (current.data.length === 0) {
      spans.push({
        start: offset,
        end: offset,
        kind: editable ? 'editable' : 'frozen',
        node: current,
        nodeStart,
        sectionId: editable ? editable.getAttribute('section-id') : null,
        pid: pidElement ? pidElement.getAttribute('pid') : null,
      });
      continue;
    }

    current.data.split(ZERO_WIDTH_CHARACTERS).forEach(content => {
      if (content.length === 0) {
        nodeStart += 1;
        return;
      }

      // `chunkStart` is an index into `content`; `nodeStart` carries the offset
      // of `content` within `current.data`, so the two together address the
      // source characters this run projects.
      const chunkOffset = nodeStart;
      const emitSource = (chunkStart, length) => {
        if (length === 0) return;

        textParts.push(content.substr(chunkStart, length));
        spans.push({
          start: offset,
          end: offset + length,
          kind: editable ? 'editable' : 'frozen',
          node: current,
          nodeStart: chunkOffset + chunkStart,
          sectionId: editable ? editable.getAttribute('section-id') : null,
          pid: pidElement ? pidElement.getAttribute('pid') : null,
        });
        offset += length;
      };

      let plainStart = 0;
      for (let cursor = 0; cursor < content.length; cursor += 1) {
        if (!MARKDOWN_SYNTAX_CHARACTER.test(content[cursor])) continue;

        emitSource(plainStart, cursor - plainStart);
        // The escape slash is synthetic projection syntax and deliberately has
        // no source span, exactly like a heading's `#` or a row's `|`. The
        // character it protects keeps its own one-character span, so every
        // projected source position still maps to a DOM position with no
        // offset compensation.
        emitSyntax('\\');
        emitSource(cursor, 1);
        plainStart = cursor + 1;
      }
      emitSource(plainStart, content.length - plainStart);
      nodeStart += content.length + 1;
    });
  }

  for (let index = openPath.length - 1; index >= 0; index -= 1) {
    closeElement(openPath[index]);
  }

  return result();
}
